import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import { apiClient } from "@/lib/apiClient";
import { Finance, Target } from "@/components/icons";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { reportsExtendedApi } from "../../services/reports-extended.api";
import { useReportData } from "../../hooks/useReportData";
import { useReportCategories } from "../../hooks/useReportCategories";
import { useReportConfig } from "../../hooks/useReportConfig";
import styles from "./RoiInvestmentReportPage.module.scss";

const PAGE_SIZE = 20;

const formatCurrency = (v: number) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  }).format(v);

const formatScore = (v: number) =>
  new Intl.NumberFormat("tr-TR", {
    maximumFractionDigits: 1,
  }).format(v);

// ---------------------------------------------------------------------------
// API types — backend service shape ile bire bir
// ---------------------------------------------------------------------------

interface RoiItem {
  educationID: string;
  title: string;
  budget: number;
  capacity: number;
  hours: number;
  enrollmentCount: number;
  completedCount: number;
  completionRatePercent: number;
  costPerEnrollment: number;
  costPerHour: number;
  avgScorePercentage: number;
  npsScore: number;
  roiScore: number;
}

interface RoiInvestmentSummary {
  totalInvestment: number;
  totalHours: number;
  avgCostPerHour: number;
  avgCostPerPerson: number;
  avgRoiScore: number;
  bestRoiTitle: string | null;
  worstRoiTitle: string | null;
}

interface RoiInvestmentResponse {
  summary: RoiInvestmentSummary;
  scatter: { x: number; y: number; label: string }[];
  byCategory: { label: string; count: number; totalInvestment: number }[];
  monthlyInvestment: { month: string; investment: number }[];
  items: RoiItem[];
  count: number;
}

interface RoiInvestmentParams {
  /** Çoklu eğitim kategorisi filtresi; alt kategoriler backend'de dahil edilir. */
  categoryIds?: string[];
  page?: number;
  limit?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  minInvestment?: number;
  departmentID?: string;
}

// Local fetcher — bu sayfaya özel; reports-extended.api'ye dokunma
const ENDPOINT = "/api/admin/reports/roi-investment";

function unwrap<T>(body: { data?: T } | T): T {
  if (body && typeof body === "object" && "data" in (body as object)) {
    const d = (body as { data?: T }).data;
    if (d !== undefined) return d as T;
  }
  return body as T;
}

const fetchRoiInvestment = async (
  params: RoiInvestmentParams,
  signal: AbortSignal,
): Promise<RoiInvestmentResponse> => {
  const { data } = await apiClient.get(ENDPOINT, { params, signal });
  return unwrap<RoiInvestmentResponse>(data);
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const RoiInvestmentReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  const categories = useReportCategories();

  const params: RoiInvestmentParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      departmentID: filter.departmentID,
      search: filter.search?.trim() || undefined,
      categoryIds: filter.categoryIds,
    }),
    [filter, page],
  );

  const fetcher = useCallback(
    (p: RoiInvestmentParams, signal: AbortSignal) =>
      fetchRoiInvestment(p, signal),
    [],
  );

  const { data, isLoading, error } = useReportData<
    RoiInvestmentResponse,
    RoiInvestmentParams
  >({ fetcher, params });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;
  const scatter = data?.scatter ?? [];
  const byCategory = data?.byCategory ?? [];
  const monthly = data?.monthlyInvestment ?? [];

  const topCategories = byCategory.slice(0, 12);

  const columns: TableColumn<RoiItem>[] = [
    {
      key: "title",
      label: t("reports.roiInvestment.headerEducation"),
      header: t("reports.roiInvestment.headerEducation"),
      accessor: (row) => row.title,
    },
    {
      key: "budget",
      label: t("reports.roiInvestment.headerInvestment"),
      header: t("reports.roiInvestment.headerInvestment"),
      accessor: (row) => formatCurrency(row.budget),
    },
    {
      key: "enrollmentCount",
      label: t("reports.roiInvestment.headerEnrollment"),
      header: t("reports.roiInvestment.headerEnrollment"),
      accessor: (row) => row.enrollmentCount,
    },
    {
      key: "completedCount",
      label: t("reports.roiInvestment.headerCompleted"),
      header: t("reports.roiInvestment.headerCompleted"),
      accessor: (row) => row.completedCount,
    },
    {
      key: "costPerEnrollment",
      label: t("reports.roiInvestment.headerCostPerPerson"),
      header: t("reports.roiInvestment.headerCostPerPerson"),
      accessor: (row) => formatCurrency(row.costPerEnrollment),
    },
    {
      key: "avgScorePercentage",
      label: t("reports.roiInvestment.headerAvgScore"),
      header: t("reports.roiInvestment.headerAvgScore"),
      accessor: (row) => `%${formatScore(row.avgScorePercentage)}`,
    },
    {
      key: "npsScore",
      label: t("reports.roiInvestment.headerNps"),
      header: t("reports.roiInvestment.headerNps"),
      accessor: (row) => (
        <span
          className={`${styles.npsCell} ${
            row.npsScore >= 0 ? styles.positive : styles.negative
          }`}
        >
          {row.npsScore}
        </span>
      ),
    },
    {
      key: "roiScore",
      label: t("reports.roiInvestment.headerRoi"),
      header: t("reports.roiInvestment.headerRoi"),
      accessor: (row) => {
        const cls =
          row.roiScore >= 70
            ? styles.high
            : row.roiScore < 40
              ? styles.low
              : "";
        return (
          <span className={`${styles.scoreCell} ${cls}`}>
            {formatScore(row.roiScore)}
          </span>
        );
      },
    },
  ];

  // R11 yapılandırılabilirliği: kolon ayarları, kayıtlı filtre setleri,
  // zamanlanmış e-posta raporu. Blok düzenini ReportDetailLayout yönetir.
  const cfg = useReportConfig<RoiItem>({
    reportKey: "ROI_INVESTMENT",
    reportTitle: t("reports.roiInvestment.title"),
    columns,
  });

  useEffect(() => {
    if (cfg.defaultFilters) setFilter(cfg.defaultFilters as ReportFilterState);
  }, [cfg.defaultFilters]);

  return (
    <ReportDetailLayout
      title={t("reports.roiInvestment.title")}
      layoutKey={`report:ROI_INVESTMENT`}
      presetSlot={cfg.presetBar(filter as Record<string, unknown>, (f) =>
        setFilter(f as ReportFilterState),
      )}
      modalSlot={cfg.scheduleModal(filter as Record<string, unknown>)}
      categoryLabel={t("reports.roiInvestment.categoryLabel")}
      description={t("reports.roiInvestment.description")}
      exportSlot={
        <>
          {cfg.toolbar}
          <ReportExportButtons
          reportType="ROI_INVESTMENT"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="roi-yatirim"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
        </>
      }
      filterSlot={
        <ReportFilterBar
          categories={categories}
          showCategory
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
        />
      }
    >
      <div className={styles.kpiGrid}>
        <ReportKPICard
          title={t("reports.roiInvestment.kpiTotalInvestment")}
          value={summary ? formatCurrency(summary.totalInvestment) : "—"}
          icon={<Finance width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.roiInvestment.kpiCostPerHour")}
          value={summary ? formatCurrency(summary.avgCostPerHour) : "—"}
          icon={<Finance width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.roiInvestment.kpiCostPerPerson")}
          value={summary ? formatCurrency(summary.avgCostPerPerson) : "—"}
          icon={<Finance width={18} height={18} />}
          accent="success"
        />
        <ReportKPICard
          title={t("reports.roiInvestment.kpiAvgRoi")}
          value={summary ? formatScore(summary.avgRoiScore) : "—"}
          icon={<Target width={18} height={18} />}
          accent={
            summary && summary.avgRoiScore >= 70
              ? "success"
              : summary && summary.avgRoiScore < 40
                ? "danger"
                : "warning"
          }
        />
      </div>

      <div className={styles.chartGrid}>
        <ReportChart
          title={t("reports.roiInvestment.chartScatterTitle")}
          type="scatter"
          isLoading={isLoading}
          isEmpty={scatter.length === 0}
          series={[
            {
              name: t("reports.roiInvestment.chartScatterSeries"),
              data: scatter.map((p) => [p.x, p.y]),
            },
          ]}
          options={{
            xaxis: {
              title: { text: t("reports.roiInvestment.chartScatterXAxis") },
              labels: {
                formatter: (val: string | number) =>
                  formatCurrency(Number(val)),
              },
            },
            yaxis: {
              title: { text: t("reports.roiInvestment.chartScatterYAxis") },
              min: 0,
              max: 100,
              tickAmount: 5,
            },
            colors: ["#4f46e5"],
            tooltip: {
              custom: ({
                dataPointIndex,
              }: {
                seriesIndex: number;
                dataPointIndex: number;
              }) => {
                const point = scatter[dataPointIndex];
                if (!point) return "";
                return `<div style="padding:8px 10px;font-size:12px;"><b>${point.label}</b><br/>${formatCurrency(
                  point.x,
                )} • ROI ${formatScore(point.y)}</div>`;
              },
            },
          }}
        />

        <ReportChart
          title={t("reports.roiInvestment.chartCategoryTitle")}
          type="bar"
          isLoading={isLoading}
          isEmpty={topCategories.length === 0}
          series={[
            {
              name: t("reports.roiInvestment.chartCategorySeries"),
              data: topCategories.map((c) => c.totalInvestment),
            },
          ]}
          options={{
            xaxis: { categories: topCategories.map((c) => c.label) },
            plotOptions: {
              bar: { horizontal: true, borderRadius: 4, barHeight: "70%" },
            },
            colors: ["#0ea5e9"],
            yaxis: {
              labels: {
                formatter: (val: number) => formatCurrency(val),
              },
            },
          }}
        />

        <div className={styles.chartFull}>
          <ReportChart
            title={t("reports.roiInvestment.chartMonthlyTitle")}
            type="area"
            isLoading={isLoading}
            isEmpty={monthly.length === 0}
            series={[
              {
                name: t("reports.roiInvestment.chartMonthlySeries"),
                data: monthly.map((m) => m.investment),
              },
            ]}
            options={{
              xaxis: { categories: monthly.map((m) => m.month) },
              colors: ["#10b981"],
              fill: { type: "gradient", gradient: { opacityFrom: 0.4, opacityTo: 0 } },
              stroke: { curve: "smooth", width: 2 },
              dataLabels: { enabled: false },
              yaxis: {
                labels: {
                  formatter: (val: number) => formatCurrency(val),
                },
              },
            }}
          />
        </div>
      </div>

      {cfg.columnSettings}

      <Table
        columns={cfg.columns}
        data={items}
        isLoading={isLoading}
        contentBasedHeight
        getRowId={(row) => row.educationID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={
          error
            ? t("reports.roiInvestment.noData")
            : t("reports.roiInvestment.noEducation")
        }
      />
    </ReportDetailLayout>
  );
};

export default RoiInvestmentReportPage;
